/** Budapest wall-clock helpers. API times are unix seconds (`*_at`, `t_post`) or integer ms durations; every
 * date shown on a WS7 page is Europe/Budapest local time (posting quotas count Budapest days). */
export const TZ = "Europe/Budapest";

const DTF = new Intl.DateTimeFormat("en-GB", {
  timeZone: TZ,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
});

export type WallParts = { y: number; m: number; d: number; hh: number; mi: number; ss: number };

export function budapestParts(unixS: number): WallParts {
  const parts = DTF.formatToParts(new Date(unixS * 1000));
  const get = (t: Intl.DateTimeFormatPartTypes) => Number(parts.find((p) => p.type === t)?.value ?? 0);
  return { y: get("year"), m: get("month"), d: get("day"), hh: get("hour") % 24, mi: get("minute"), ss: get("second") };
}

const p2 = (n: number) => String(n).padStart(2, "0");

/** YYYY-MM-DD of the Budapest day containing unixS. */
export function budapestDay(unixS: number): string {
  const p = budapestParts(unixS);
  return `${p.y}-${p2(p.m)}-${p2(p.d)}`;
}

/** HH:MM Budapest time. */
export function budapestTime(unixS: number): string {
  const p = budapestParts(unixS);
  return `${p2(p.hh)}:${p2(p.mi)}`;
}

/** "YYYY-MM-DD HH:MM" in Budapest time; "" for null. */
export function fmtWhen(unixS: number | null | undefined): string {
  if (unixS == null || !Number.isFinite(unixS)) return "";
  return `${budapestDay(unixS)} ${budapestTime(unixS)}`;
}

export function isoUtc(unixS: number): string {
  return new Date(unixS * 1000).toISOString();
}

/** Seconds east of UTC that Budapest is at the instant unixS (3600 in winter, 7200 in summer). */
export function budapestOffsetS(unixS: number): number {
  const p = budapestParts(unixS);
  return Date.UTC(p.y, p.m - 1, p.d, p.hh, p.mi, p.ss) / 1000 - Math.floor(unixS);
}

function parseDay(day: string): [number, number, number] {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!m) throw new Error(`bad day ${day}`);
  return [Number(m[1]), Number(m[2]), Number(m[3])];
}

export function isDay(s: string | null | undefined): s is string {
  if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const [y, m, d] = parseDay(s);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

export function isTime(s: string | null | undefined): s is string {
  return !!s && /^([01]\d|2[0-3]):[0-5]\d$/.test(s);
}

/** Unix seconds of a Budapest wall-clock time (DST-correct; a time inside the spring-forward gap moves forward). */
export function budapestToUnix(day: string, time = "00:00"): number {
  const [y, m, d] = parseDay(day);
  const [hh, mi] = time.split(":").map(Number) as [number, number];
  const naive = Date.UTC(y, m - 1, d, hh, mi) / 1000;
  let t = naive - budapestOffsetS(naive);
  t = naive - budapestOffsetS(t);
  return t;
}

export function addDays(day: string, n: number): string {
  const [y, m, d] = parseDay(day);
  const dt = new Date(Date.UTC(y, m - 1, d + n));
  return `${dt.getUTCFullYear()}-${p2(dt.getUTCMonth() + 1)}-${p2(dt.getUTCDate())}`;
}

/** 0 = Monday … 6 = Sunday. */
export function weekdayIndex(day: string): number {
  const [y, m, d] = parseDay(day);
  return (new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7;
}

export function mondayOf(day: string): string {
  return addDays(day, -weekdayIndex(day));
}

export function weekDays(monday: string): string[] {
  return Array.from({ length: 7 }, (_, i) => addDays(monday, i));
}

const WD = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
export function weekdayName(day: string): string {
  return WD[weekdayIndex(day)] ?? "";
}

export function todayBudapest(nowMs: number = Date.now()): string {
  return budapestDay(Math.floor(nowMs / 1000));
}

/** m:ss from integer milliseconds (truncated to whole seconds); "" for null. */
export function fmtDuration(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms)) return "";
  const neg = ms < 0;
  const s = Math.floor(Math.abs(ms) / 1000);
  return `${neg ? "-" : ""}${Math.floor(s / 60)}:${p2(s % 60)}`;
}

const NUM = new Intl.NumberFormat("en-US");
export function fmtNum(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return "";
  return NUM.format(n);
}

export function fmtPct(x: number | null | undefined): string {
  if (x == null || !Number.isFinite(x)) return "";
  return `${Math.round(x * 100)}%`;
}

/** "+1,234" / "-12" / "0" for deltas. */
export function fmtDelta(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return "";
  return n > 0 ? `+${fmtNum(n)}` : fmtNum(n);
}
